import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, StickyNote, Pencil, Trash2, Save, X, Loader2 } from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";

const NOTE_TYPES = [
  { value: "general_note", label: "Allgemeine Notiz" },
  { value: "conversation_note", label: "Gesprächsnotiz" },
  { value: "phone_note", label: "Telefonnotiz" },
  { value: "trade_fair_note", label: "Messengespräch" },
  { value: "appointment_note", label: "Terminnotiz" },
  { value: "internal_assessment", label: "Interne Einschätzung" },
  { value: "objection_risk", label: "Einwand / Risiko" },
  { value: "decision", label: "Entscheidung" },
  { value: "partner_note", label: "Partnernotiz" },
  { value: "dev_review_note", label: "Dev-Prüfnotiz" },
  { value: "manual_initial_note", label: "Erstnotiz" },
];
const NOTE_LABEL = Object.fromEntries(NOTE_TYPES.map((o) => [o.value, o.label]));

const inputClass = "w-full rounded border border-white/10 bg-[#0B0F10] px-3 py-2 text-sm text-zinc-100 outline-none transition focus:border-emerald-500/40";
const textareaClass = `${inputClass} min-h-[100px] resize-y leading-relaxed`;

function headers() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function NotesTab({ leadId, onChange }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [body, setBody] = useState("");
  const [noteType, setNoteType] = useState("general_note");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [editType, setEditType] = useState("general_note");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/crm/leads/${leadId}/notes`, {
        headers: headers(), timeout: 15000,
      });
      setNotes(r.data?.items || []);
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/crm/leads/${leadId}/notes`,
        { body_markdown: body.trim(), note_type: noteType },
        { headers: headers(), timeout: 15000 }
      );
      toast.success("Notiz gespeichert");
      setBody(""); setNoteType("general_note"); setShowForm(false);
      await load(); onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const startEdit = (n) => {
    setEditId(n.id);
    setEditBody(n.body_markdown);
    setEditType(n.note_type);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/crm/notes/${editId}`,
        { body_markdown: editBody.trim(), note_type: editType },
        { headers: headers(), timeout: 15000 }
      );
      toast.success("Notiz aktualisiert");
      setEditId(null);
      await load(); onChange?.();
    } catch (err) {
      toast.error("Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Notiz wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/crm/notes/${id}`, { headers: headers() });
      toast.success("Notiz gelöscht");
      await load(); onChange?.();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <section data-testid="journey-notes-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
          <StickyNote className="h-4 w-4 text-emerald-400" />
          Notizen ({notes.length})
        </div>
        {!showForm ? (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            data-testid="notes-add-button"
            className="inline-flex items-center gap-1 rounded bg-emerald-500 px-3 py-1.5 text-xs font-medium text-zinc-950 hover:bg-emerald-400"
          >
            <Plus className="h-3.5 w-3.5" />
            Notiz anlegen
          </button>
        ) : null}
      </div>

      {showForm ? (
        <div data-testid="notes-form" className="space-y-3 rounded-md border border-white/10 bg-white/[0.02] p-4">
          <select value={noteType} onChange={(e) => setNoteType(e.target.value)} data-testid="notes-form-type" className={inputClass}>
            {NOTE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <textarea
            value={body} onChange={(e) => setBody(e.target.value)}
            data-testid="notes-form-body" placeholder="Inhalt der Notiz…"
            className={textareaClass} autoFocus
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowForm(false); setBody(""); }} className="rounded border border-white/10 px-3 py-1.5 text-xs text-zinc-300 hover:bg-white/5">
              Abbrechen
            </button>
            <button type="button" disabled={saving || !body.trim()} onClick={submit} data-testid="notes-form-save" className="inline-flex items-center gap-1.5 rounded bg-emerald-500 px-3 py-1.5 text-xs font-medium text-zinc-950 hover:bg-emerald-400 disabled:opacity-40">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Notiz speichern
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-white/[0.03]" />)}
        </div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-zinc-500">Noch keine Notiz erfasst.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} data-testid={`note-${n.id}`} className="space-y-2 rounded-md border border-white/[0.06] bg-white/[0.01] p-4">
              <div className="flex items-start justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-300">
                    {NOTE_LABEL[n.note_type] || n.note_type}
                  </span>
                  <span className="text-zinc-500">{formatDate(n.created_at)}</span>
                  {n.edited_at ? <span className="text-zinc-600">· bearbeitet {formatDate(n.edited_at)}</span> : null}
                </div>
                {editId === n.id ? null : (
                  <div className="flex gap-1">
                    <button type="button" onClick={() => startEdit(n)} data-testid={`note-edit-${n.id}`} className="rounded p-1 text-zinc-400 hover:bg-white/5 hover:text-zinc-100" aria-label="Bearbeiten">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={() => remove(n.id)} data-testid={`note-delete-${n.id}`} className="rounded p-1 text-zinc-400 hover:bg-red-500/10 hover:text-red-300" aria-label="Löschen">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              {editId === n.id ? (
                <div className="space-y-2">
                  <select value={editType} onChange={(e) => setEditType(e.target.value)} className={inputClass}>
                    {NOTE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} className={textareaClass} />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditId(null)} className="rounded border border-white/10 px-3 py-1 text-xs text-zinc-300">
                      <X className="mr-1 inline h-3 w-3" />Abbrechen
                    </button>
                    <button type="button" disabled={saving} onClick={saveEdit} data-testid={`note-save-${n.id}`} className="inline-flex items-center gap-1 rounded bg-emerald-500 px-3 py-1 text-xs font-medium text-zinc-950 hover:bg-emerald-400">
                      <Save className="h-3 w-3" />Speichern
                    </button>
                  </div>
                </div>
              ) : (
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-200">{n.body_markdown}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
